import { cn } from '@commenda/utils';
import { useMemo, useState } from 'react';

import {
  filterConversationsByTitle,
  formatConversationAge,
  groupConversationsForViewer,
  type PanelHistoryConversation,
} from './conversation-history.helper';

const STATUS_ROW_CLASS_NAMES =
  'px-lg py-md txt-small-regular text-text-placeholder';

interface ConversationHistoryListProps {
  conversations: readonly PanelHistoryConversation[];
  activeConversationId: string | null;
  onSelect: (conversationId: string) => void;
  /** Splits the viewer's own conversations from ones shared with them. Omit on a surface with no
   *  sharing, and the list is grouped by recency alone. */
  viewerId?: string;
  isLoading?: boolean;
  hasError?: boolean;
  searchPlaceholder?: string;
}

/**
 * Past conversations with a search box and recency bands. The one list every history surface shows:
 * the chat panel's popover and a side panel mount this same component, so they cannot drift.
 */
export const ConversationHistoryList = ({
  conversations,
  activeConversationId,
  onSelect,
  viewerId,
  isLoading = false,
  hasError = false,
  searchPlaceholder = 'Chat history',
}: ConversationHistoryListProps) => {
  const [query, setQuery] = useState('');

  const groups = useMemo(
    () =>
      groupConversationsForViewer(
        filterConversationsByTitle(conversations, query),
        viewerId
      ),
    [conversations, query, viewerId]
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Outside the scroll container, so it stays put however far the list runs. */}
      <div className="shrink-0 px-lg py-md">
        <input
          type="text"
          autoComplete="off"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={searchPlaceholder}
          aria-label="Search chats"
          className="w-full appearance-none border-0 bg-transparent p-none txt-small-regular text-text-primary placeholder:text-text-placeholder focus:outline-none focus:ring-0"
        />
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto py-sm">
        {isLoading && <p className={STATUS_ROW_CLASS_NAMES}>Loading…</p>}
        {!isLoading && hasError && (
          <p className={cn(STATUS_ROW_CLASS_NAMES, 'text-text-error')}>
            Could not load chat history.
          </p>
        )}
        {!isLoading && !hasError && groups.length === 0 && (
          <p className={STATUS_ROW_CLASS_NAMES}>
            {query ? 'No chats match your search.' : 'No conversations yet.'}
          </p>
        )}
        {groups.map((group, index) => (
          // Each recency band is its own section, ruled off from the one above it.
          <div key={group.heading}>
            {index > 0 && (
              <div className="py-sm">
                <div className="h-[0.5px] bg-border-secondary" />
              </div>
            )}
            {/* Sticks while its own rows scroll past, so the band you are reading is always named.
                Needs an opaque background or the rows show through it. */}
            <p className="sticky top-none z-10 bg-bg-primary px-lg py-md txt-extra-small-medium text-text-secondary">
              {group.heading}
            </p>
            <div className="px-sm">
              {group.conversations.map((conversation) => (
                <button
                  key={conversation.id}
                  type="button"
                  onClick={() => onSelect(conversation.id)}
                  // Top-aligned, so the age reads against the first line of a title rather than
                  // floating beside a wrapped one.
                  className={cn(
                    'flex w-full items-start gap-sm rounded-sm py-md pl-md pr-lg text-left transition-colors',
                    conversation.id === activeConversationId
                      ? 'bg-bg-secondary'
                      : 'hover:bg-bg-secondary'
                  )}
                >
                  <span className="min-w-0 flex-1 truncate txt-small-medium text-text-primary">
                    {conversation.title ?? 'New chat'}
                  </span>
                  <span className="flex h-5 shrink-0 items-center txt-extra-small-regular text-text-tertiary">
                    {formatConversationAge(conversation.updatedAt)}
                  </span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
